/**
 * DataTable — enterprise table com sort, filter, density, column visibility,
 * pagination, row click → detail, bulk selection com checkbox.
 */
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table";
import {
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Columns3,
  LayoutGrid,
  Rows3,
  X,
} from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { SkeletonTable } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  loading?: boolean;
  searchPlaceholder?: string;
  enableSearch?: boolean;
  enableDensityToggle?: boolean;
  enableColumnVisibility?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
  onRowClick?: (row: TData) => void;
  columnLabels?: Record<string, string>;
  toolbarRight?: ReactNode;
  /** Habilita seleção via checkbox + bulk actions */
  enableSelection?: boolean;
  /** Renderiza barra de actions em massa quando algo está selecionado */
  bulkActions?: (selectedRows: TData[], clear: () => void) => ReactNode;
  /** Pra rowSelection — função pra extrair id estável (default: usa row.id) */
  getRowId?: (row: TData) => string;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  loading,
  searchPlaceholder = "Buscar...",
  enableSearch = true,
  enableDensityToggle = true,
  enableColumnVisibility = true,
  emptyTitle = "Nada por aqui ainda",
  emptyDescription = "Crie o primeiro registro pra começar.",
  emptyAction,
  onRowClick,
  columnLabels = {},
  toolbarRight,
  enableSelection,
  bulkActions,
  getRowId,
}: DataTableProps<TData, TValue>) {
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [density, setDensity] = useState<"compact" | "comfortable">("comfortable");

  // Inject checkbox column if selection is enabled
  const finalColumns = useMemo<ColumnDef<TData, TValue>[]>(() => {
    if (!enableSelection) return columns;
    const selectCol: ColumnDef<TData, TValue> = {
      id: "_select",
      header: ({ table }) => (
        <input
          type="checkbox"
          checked={table.getIsAllPageRowsSelected()}
          onChange={(e) => table.toggleAllPageRowsSelected(e.target.checked)}
          className="rounded border-slate-300 cursor-pointer"
          onClick={(e) => e.stopPropagation()}
          aria-label="Selecionar todos"
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={row.getIsSelected()}
          onChange={(e) => row.toggleSelected(e.target.checked)}
          onClick={(e) => e.stopPropagation()}
          className="rounded border-slate-300 cursor-pointer"
          aria-label="Selecionar linha"
        />
      ),
      enableSorting: false,
      size: 36,
    };
    return [selectCol, ...columns];
  }, [columns, enableSelection]);

  const table = useReactTable({
    data,
    columns: finalColumns,
    state: { sorting, columnVisibility, globalFilter, rowSelection },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: getRowId,
    enableRowSelection: enableSelection,
    initialState: { pagination: { pageSize: 20 } },
  });

  const rows = table.getRowModel().rows;
  const selectedRows = table.getSelectedRowModel().rows.map((r) => r.original);

  const showEmpty = !loading && data.length === 0;
  const showFilterEmpty = !loading && data.length > 0 && rows.length === 0;
  const colCount = finalColumns.length;
  const hasSelection = selectedRows.length > 0;

  const clearSelection = () => setRowSelection({});

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      {(enableSearch || enableColumnVisibility || enableDensityToggle || toolbarRight) && (
        <div className="flex flex-wrap items-center gap-2">
          {enableSearch && (
            <div className="flex-1 min-w-[240px] max-w-md">
              <Input
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                placeholder={searchPlaceholder}
                className="h-9"
              />
            </div>
          )}
          <div className="flex items-center gap-2 ml-auto">
            {toolbarRight}
            {enableDensityToggle && (
              <Button
                variant="outline"
                size="icon-sm"
                onClick={() => setDensity((d) => (d === "compact" ? "comfortable" : "compact"))}
                title={density === "compact" ? "Aumentar espaçamento" : "Diminuir espaçamento"}
              >
                {density === "compact" ? <LayoutGrid className="h-4 w-4" /> : <Rows3 className="h-4 w-4" />}
              </Button>
            )}
            {enableColumnVisibility && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon-sm" title="Colunas visíveis">
                    <Columns3 className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuLabel>Colunas</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {table.getAllLeafColumns().filter((c) => c.id !== "_select").map((col) => (
                    <DropdownMenuCheckboxItem
                      key={col.id}
                      checked={col.getIsVisible()}
                      onCheckedChange={(v) => col.toggleVisibility(!!v)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {columnLabels[col.id] ?? col.id}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      )}

      {/* Bulk Actions Bar */}
      {hasSelection && bulkActions && (
        <div className="rounded-xl border border-primary-200 dark:border-primary-500/20 bg-primary-50/60 dark:bg-primary-500/[0.06] px-4 py-2.5 flex items-center justify-between gap-3 animate-fade-in">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={clearSelection}
              title="Limpar seleção"
            >
              <X className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium text-primary-800 dark:text-primary-200">
              {selectedRows.length} selecionado{selectedRows.length > 1 ? "s" : ""}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {bulkActions(selectedRows, clearSelection)}
          </div>
        </div>
      )}

      {/* Body */}
      {loading && <SkeletonTable cols={colCount} rows={6} />}
      {showEmpty && (
        <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />
      )}

      {!loading && data.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 backdrop-blur">
          <div className="overflow-x-auto">
            <table className="w-full caption-bottom text-sm">
              <thead className="bg-slate-50/80 dark:bg-white/[0.03] sticky top-0 z-10">
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id} className="border-b border-slate-200/60 dark:border-white/10">
                    {hg.headers.map((h) => {
                      const canSort = h.column.getCanSort();
                      const sortDir = h.column.getIsSorted();
                      return (
                        <th
                          key={h.id}
                          className="text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground px-4 py-3"
                          style={{ width: h.getSize() !== 150 ? h.getSize() : undefined }}
                        >
                          {h.isPlaceholder ? null : canSort && h.column.id !== "_select" ? (
                            <button
                              type="button"
                              onClick={h.column.getToggleSortingHandler()}
                              className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                            >
                              {flexRender(h.column.columnDef.header, h.getContext())}
                              {sortDir === "asc" ? (
                                <ChevronUp className="h-3 w-3" />
                              ) : sortDir === "desc" ? (
                                <ChevronDown className="h-3 w-3" />
                              ) : (
                                <ArrowUpDown className="h-3 w-3 opacity-40" />
                              )}
                            </button>
                          ) : (
                            flexRender(h.column.columnDef.header, h.getContext())
                          )}
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {showFilterEmpty && (
                  <tr>
                    <td colSpan={colCount} className="px-4 py-12 text-center text-sm text-muted-foreground">
                      Nenhum resultado pra <span className="font-mono">"{globalFilter}"</span>
                    </td>
                  </tr>
                )}
                {rows.map((row, idx) => (
                  <tr
                    key={row.id}
                    onClick={() => onRowClick?.(row.original)}
                    className={cn(
                      "group border-b border-slate-200/40 dark:border-white/5 last:border-0",
                      "transition-colors relative",
                      onRowClick && "cursor-pointer hover:bg-slate-50/60 dark:hover:bg-white/[0.03]",
                      idx % 2 === 1 && "bg-slate-50/30 dark:bg-white/[0.015]",
                      row.getIsSelected() && "bg-primary-50/60 dark:bg-primary-500/[0.05]",
                    )}
                  >
                    {row.getVisibleCells().map((cell, cellIdx) => (
                      <td
                        key={cell.id}
                        className={cn(
                          "px-4 align-middle",
                          density === "compact" ? "py-2" : "py-3",
                          cell.column.id === "_select" && "w-9",
                        )}
                      >
                        <div className={cn(
                          "flex items-center",
                          cellIdx === row.getVisibleCells().length - 1 && onRowClick && "justify-between",
                        )}>
                          <div className="min-w-0 flex-1">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </div>
                          {cellIdx === row.getVisibleCells().length - 1 && onRowClick && (
                            <ChevronRight className="ml-2 h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all shrink-0" />
                          )}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {table.getPageCount() > 1 && (
            <div className="flex items-center justify-between gap-2 border-t border-slate-200/60 dark:border-white/10 bg-slate-50/40 dark:bg-white/[0.02] px-4 py-2.5">
              <p className="text-xs text-muted-foreground">
                Mostrando {table.getRowModel().rows.length} de {table.getFilteredRowModel().rows.length} resultados
              </p>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" disabled={!table.getCanPreviousPage()} onClick={() => table.previousPage()}>
                  Anterior
                </Button>
                <span className="text-xs text-muted-foreground tabular-nums px-2">
                  {table.getState().pagination.pageIndex + 1} / {table.getPageCount()}
                </span>
                <Button variant="ghost" size="sm" disabled={!table.getCanNextPage()} onClick={() => table.nextPage()}>
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
